if (!customElements.get('blog-search')) {
  customElements.define(
    'blog-search',
    class BlogSearch extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.form = this.querySelector('[data-blog-search-form]');
        this.input = this.querySelector('[data-blog-search-input]');
        this.clearButton = this.querySelector('[data-blog-search-clear]');
        this.panel = this.querySelector('[data-blog-search-panel]');
        this.status = this.querySelector('[data-blog-search-status]');
        this.list = this.querySelector('[data-blog-search-results]');
        this.abortController = new AbortController();

        if (!this.form || !this.input || !this.clearButton || !this.panel || !this.status || !this.list) return;

        const signal = this.abortController.signal;
        this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
        this.input.addEventListener('input', () => this.handleInput(), { signal });
        this.input.addEventListener('focus', () => this.handleFocus(), { signal });
        this.clearButton?.addEventListener('click', () => this.clear(), { signal });
        this.panel.addEventListener('touchstart', (event) => this.handlePanelTouchStart(event), {
          passive: true,
          signal,
        });
        this.panel.addEventListener('touchmove', (event) => this.handlePanelTouchMove(event), {
          passive: false,
          signal,
        });
        this.panel.addEventListener('touchend', () => this.handlePanelTouchEnd(), { signal });
        this.panel.addEventListener('touchcancel', () => this.handlePanelTouchEnd(), { signal });
        document.addEventListener('click', (event) => this.handleOutsideClick(event), { signal });
        document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      }

      disconnectedCallback() {
        window.clearTimeout(this.debounceTimer);
        this.abortController?.abort();
        this.requestController?.abort();
        this.initialized = false;
      }

      handleInput() {
        const hasQuery = this.input.value.trim().length > 0;
        this.clearButton.hidden = !hasQuery;
        window.clearTimeout(this.debounceTimer);

        if (!hasQuery) {
          this.close();
          return;
        }

        this.debounceTimer = window.setTimeout(() => this.search(), 220);
      }

      handleFocus() {
        if (this.input.value.trim() && this.list.childElementCount > 0) {
          this.open();
        }
      }

      handleSubmit(event) {
        event.preventDefault();
        window.clearTimeout(this.debounceTimer);

        if (!this.input.value.trim()) {
          this.input.focus();
          return;
        }

        this.search();
      }

      handleOutsideClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      handlePanelTouchStart(event) {
        this.lastTouchY = event.touches[0]?.clientY;
      }

      handlePanelTouchMove(event) {
        const currentTouchY = event.touches[0]?.clientY;
        if (typeof currentTouchY !== 'number' || typeof this.lastTouchY !== 'number') return;

        const scrollDelta = this.lastTouchY - currentTouchY;
        this.lastTouchY = currentTouchY;

        const maxScrollTop = Math.max(0, this.panel.scrollHeight - this.panel.clientHeight);
        const isAtTop = this.panel.scrollTop <= 1;
        const isAtBottom = this.panel.scrollTop >= maxScrollTop - 1;
        const shouldScrollPage =
          maxScrollTop <= 1 || (scrollDelta < 0 && isAtTop) || (scrollDelta > 0 && isAtBottom);

        if (!shouldScrollPage) return;

        event.preventDefault();
        window.scrollBy(0, scrollDelta);
      }

      handlePanelTouchEnd() {
        this.lastTouchY = null;
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && !this.panel.hidden) {
          this.close();
          this.input.focus();
          return;
        }

        if (this.panel.hidden || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;

        const links = [...this.list.querySelectorAll('a')];
        if (links.length === 0) return;

        if (event.target === this.input) {
          event.preventDefault();
          links[event.key === 'ArrowDown' ? 0 : links.length - 1].focus();
          return;
        }

        const currentIndex = links.indexOf(event.target);
        if (currentIndex < 0) return;

        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        links[(currentIndex + direction + links.length) % links.length].focus();
      }

      async search() {
        const query = this.input.value.trim();
        if (!query) return;

        this.setLoading();
        const requestedQuery = query;

        try {
          const articles = await this.loadArticles();
          if (this.input.value.trim() !== requestedQuery) return;

          const results = this.filterArticles(articles, requestedQuery);
          this.renderResults(results);
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.renderError();
        }
      }

      async loadArticles() {
        if (this.articles) return this.articles;
        if (this.articlesPromise) return this.articlesPromise;

        this.requestController = new AbortController();
        this.articlesPromise = this.fetchAllPages(this.requestController.signal)
          .then((articles) => {
            this.articles = articles;
            return articles;
          })
          .finally(() => {
            this.articlesPromise = null;
          });

        return this.articlesPromise;
      }

      async fetchAllPages(signal) {
        const firstPage = await this.fetchPage(1, signal);
        const articles = [...firstPage.articles];
        const remainingRequests = [];

        for (let page = 2; page <= firstPage.pages; page += 1) {
          remainingRequests.push(this.fetchPage(page, signal));
        }

        const remainingPages = await Promise.all(remainingRequests);
        for (const page of remainingPages) articles.push(...page.articles);

        return articles;
      }

      async fetchPage(page, signal) {
        const url = new URL(this.dataset.endpoint, window.location.origin);
        url.searchParams.set('view', 'search-index');
        url.searchParams.set('page', String(page));

        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          signal,
        });

        if (!response.ok) throw new Error(`Blog search request failed: ${response.status}`);

        const data = await response.json();
        return {
          articles: Array.isArray(data.articles) ? data.articles : [],
          pages: Number(data.pages) || 1,
        };
      }

      filterArticles(articles, query) {
        const tokens = this.normalize(query).split(/\s+/).filter(Boolean);
        const oneYearAgo = Date.now() / 1000 - 365 * 24 * 60 * 60;

        return articles
          .map((article, index) => {
            if (article.publishedAtTimestamp && article.publishedAtTimestamp < oneYearAgo) return null;

            const title = this.normalize(article.title);
            const tags = this.normalize(article.tags);
            const searchText = this.normalize(article.searchText);
            const haystack = `${title} ${tags} ${searchText}`;

            if (!tokens.every((token) => haystack.includes(token))) return null;

            let score = 0;
            for (const token of tokens) {
              if (title.startsWith(token)) score += 8;
              else if (title.includes(token)) score += 5;
              if (tags.includes(token)) score += 3;
              if (searchText.includes(token)) score += 1;
            }

            return { article, index, score };
          })
          .filter(Boolean)
          .sort((a, b) => b.score - a.score || a.index - b.index)
          .map((result) => result.article);
      }

      normalize(value) {
        return String(value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase();
      }

      setLoading() {
        this.list.replaceChildren();
        this.status.textContent = this.dataset.loadingMessage;
        this.classList.add('is-loading');
        this.open();
      }

      renderResults(results) {
        const fragment = document.createDocumentFragment();
        for (const article of results) fragment.append(this.createResult(article));

        this.list.replaceChildren(fragment);
        this.classList.remove('is-loading', 'has-error');
        this.classList.toggle('has-results', results.length > 0);

        if (results.length === 0) {
          this.status.textContent = this.dataset.noResultsMessage;
        } else {
          const label = results.length === 1 ? this.dataset.resultLabel : this.dataset.resultsLabel;
          this.status.textContent = `${results.length} ${label}`;
        }

        this.open();
      }

      createResult(article) {
        const item = document.createElement('li');
        item.className = 'seo-blog-search__result';

        const link = document.createElement('a');
        link.className = 'seo-blog-search__result-link';
        link.href = article.url;

        const media = document.createElement('span');
        media.className = 'seo-blog-search__result-media';

        if (article.image) {
          const image = document.createElement('img');
          image.className = 'seo-blog-search__result-image';
          image.src = article.image;
          image.alt = article.imageAlt || article.title;
          image.loading = 'lazy';
          image.width = 88;
          image.height = 66;
          media.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'seo-blog-search__result-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.textContent = String(article.title || '').slice(0, 1);
          media.append(placeholder);
        }

        const copy = document.createElement('span');
        copy.className = 'seo-blog-search__result-copy';

        const title = document.createElement('span');
        title.className = 'seo-blog-search__result-title';
        title.textContent = article.title;
        copy.append(title);

        const meta = document.createElement('span');
        meta.className = 'seo-blog-search__result-meta';
        meta.textContent = [article.tag, article.publishedAt].filter(Boolean).join(' · ');
        copy.append(meta);

        const arrow = document.createElement('span');
        arrow.className = 'seo-blog-search__result-arrow';
        arrow.setAttribute('aria-hidden', 'true');

        link.append(media, copy, arrow);
        item.append(link);
        return item;
      }

      renderError() {
        this.list.replaceChildren();
        this.status.textContent = this.dataset.errorMessage;
        this.classList.remove('is-loading', 'has-results');
        this.classList.add('has-error');
        this.open();
      }

      clear() {
        window.clearTimeout(this.debounceTimer);
        this.input.value = '';
        this.clearButton.hidden = true;
        this.close();
        this.input.focus();
      }

      open() {
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.panel.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-loading', 'has-results', 'has-error');
      }
    }
  );
}
